import React, { useState } from 'react';
import { Plus, MessageSquare, Trash2, Loader2, PanelLeftClose, PanelLeft, Search } from 'lucide-react';
import { ChatSession } from '../types';

interface Props {
  sessions: ChatSession[];
  activeSessionId: number | null;
  isLoading: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onSelectSession: (id: number) => void;
  onNewChat: () => void;
  onDeleteSession: (id: number) => void;
  userName?: string;
  userEmail?: string;
}

export const ChatSessionSidebar: React.FC<Props> = ({
  sessions,
  activeSessionId,
  isLoading,
  isCollapsed,
  onToggleCollapse,
  onSelectSession,
  onNewChat,
  onDeleteSession,
  userName,
  userEmail,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredSession, setHoveredSession] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return 'Previous 7 Days';
    if (days < 30) return 'Previous 30 Days';
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const filteredSessions = sessions.filter(
    (s) => !searchQuery || s.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedSessions = filteredSessions.reduce((acc, session) => {
    const group = formatDate(session.updated_at);
    if (!acc[group]) acc[group] = [];
    acc[group].push(session);
    return acc;
  }, {} as Record<string, ChatSession[]>);

  const groupOrder = ['Today', 'Yesterday', 'Previous 7 Days', 'Previous 30 Days'];

  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmDelete === id) {
      onDeleteSession(id);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(id);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  const userInitials = userName
    ? userName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : 'U';

  if (isCollapsed) {
    return (
      <aside className="w-0 md:w-16 bg-gray-50 border-r border-gray-200 flex flex-col items-center py-3 transition-all duration-300 overflow-hidden">
        <button
          onClick={onToggleCollapse}
          className="p-2.5 text-gray-500 hover:text-[#07E5D1] hover:bg-[#E8FCFA] rounded-lg transition-colors mb-4"
          title="Expand sidebar"
        >
          <PanelLeft className="w-5 h-5" />
        </button>
        <button
          onClick={onNewChat}
          className="p-2.5 text-gray-500 hover:text-[#07E5D1] hover:bg-[#E8FCFA] rounded-lg transition-colors"
          title="New chat"
        >
          <Plus className="w-5 h-5" />
        </button>
        <div className="flex-1" />
        <div className="w-8 h-8 rounded-full bg-[#07E5D1] flex items-center justify-center text-white text-xs font-semibold">
          {userInitials}
        </div>
      </aside>
    );
  }

  return (
    <aside className="w-72 bg-gray-50 border-r border-gray-200 flex flex-col h-full transition-all duration-300">
      {/* Header */}
      <div className="p-3 flex items-center justify-between border-b border-gray-200">
        <button
          onClick={onToggleCollapse}
          className="p-2 text-gray-500 hover:text-[#07E5D1] hover:bg-[#E8FCFA] rounded-lg transition-colors"
          title="Collapse sidebar"
        >
          <PanelLeftClose className="w-5 h-5" />
        </button>
        <button
          onClick={onNewChat}
          className="flex items-center gap-2 px-3 py-2 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-lg transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          New chat
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search chats..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#07E5D1] focus:ring-1 focus:ring-[#07E5D1]/20 transition-colors"
          />
        </div>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto px-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-[#07E5D1]" />
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-12 px-4">
            <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">{searchQuery ? 'No matching chats' : 'No conversations yet'}</p>
            {!searchQuery && <p className="text-xs text-gray-400 mt-1">Start a new chat to begin</p>}
          </div>
        ) : (
          <div className="space-y-4 pb-4">
            {groupOrder
              .filter((group) => groupedSessions[group])
              .map((group) => (
                <div key={group}>
                  <h3 className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">{group}</h3>
                  <div className="space-y-0.5">
                    {groupedSessions[group].map((session) => (
                      <div
                        key={session.id}
                        className={`group relative flex items-center mx-1 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                          activeSessionId === session.id
                            ? 'bg-[#E8FCFA] text-[#07E5D1] border border-[#07E5D1]/20'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        onClick={() => onSelectSession(session.id)}
                        onMouseEnter={() => setHoveredSession(session.id)}
                        onMouseLeave={() => {
                          setHoveredSession(null);
                          if (confirmDelete === session.id) setConfirmDelete(null);
                        }}
                      >
                        <MessageSquare
                          className={`w-4 h-4 mr-3 flex-shrink-0 ${
                            activeSessionId === session.id ? 'text-[#07E5D1]' : 'text-gray-400'
                          }`}
                        />
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <p className="text-sm truncate pr-8">{session.title || 'New conversation'}</p>
                        </div>

                        {(hoveredSession === session.id || activeSessionId === session.id) && (
                          <div className="absolute right-2 flex items-center gap-0.5">
                            <button
                              onClick={(e) => handleDelete(session.id, e)}
                              className={`p-1.5 rounded transition-colors ${
                                confirmDelete === session.id
                                  ? 'bg-red-100 text-red-500 hover:bg-red-200'
                                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                              }`}
                              title={confirmDelete === session.id ? 'Click again to confirm' : 'Delete'}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

            {Object.entries(groupedSessions)
              .filter(([group]) => !groupOrder.includes(group))
              .map(([group, groupSessions]) => (
                <div key={group}>
                  <h3 className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">{group}</h3>
                  <div className="space-y-0.5">
                    {groupSessions.map((session) => (
                      <div
                        key={session.id}
                        className={`group relative flex items-center mx-1 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                          activeSessionId === session.id
                            ? 'bg-[#E8FCFA] text-[#07E5D1] border border-[#07E5D1]/20'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        onClick={() => onSelectSession(session.id)}
                        onMouseEnter={() => setHoveredSession(session.id)}
                        onMouseLeave={() => {
                          setHoveredSession(null);
                          if (confirmDelete === session.id) setConfirmDelete(null);
                        }}
                      >
                        <MessageSquare
                          className={`w-4 h-4 mr-3 flex-shrink-0 ${
                            activeSessionId === session.id ? 'text-[#07E5D1]' : 'text-gray-400'
                          }`}
                        />
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <p className="text-sm truncate pr-8">{session.title || 'New conversation'}</p>
                        </div>

                        {(hoveredSession === session.id || activeSessionId === session.id) && (
                          <div className="absolute right-2 flex items-center gap-0.5">
                            <button
                              onClick={(e) => handleDelete(session.id, e)}
                              className={`p-1.5 rounded transition-colors ${
                                confirmDelete === session.id
                                  ? 'bg-red-100 text-red-500 hover:bg-red-200'
                                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                              }`}
                              title={confirmDelete === session.id ? 'Click again to confirm' : 'Delete'}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* User Profile Footer */}
      <div className="p-3 border-t border-gray-200 bg-white">
        <div className="flex items-center gap-3 px-2 py-1">
          <div className="w-9 h-9 rounded-full bg-[#07E5D1] flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
            {userInitials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-900 font-medium truncate">{userName || 'User'}</p>
            <p className="text-xs text-gray-500 truncate">{userEmail || ''}</p>
          </div>
        </div>
      </div>
    </aside>
  );
};
